import React, { useState } from 'react';
import { Trash2, RefreshCw, Clock, Search, ArrowLeft, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Input,
} from '@license-management/ui';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/Toast';
import { useDeletedReleases, type DeletedRelease } from '../api/hooks/updates/useReleaseQuery';
import { useRestoreRelease, usePermanentlyDeleteRelease } from '../api/hooks/updates/useReleaseMutation';

export function ReleaseTrash() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRelease, setSelectedRelease] = useState<DeletedRelease | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] = useState(false);

  // Fetch deleted releases
  const { data, isLoading, error } = useDeletedReleases({
    page,
    pageSize: 20,
    search: searchTerm,
  });

  // Restore mutation
  const restoreMutation = useRestoreRelease();

  // Permanent delete mutation
  const permanentDeleteMutation = usePermanentlyDeleteRelease();

  // Handle restore success/error
  React.useEffect(() => {
    if (restoreMutation.isSuccess) {
      setShowRestoreDialog(false);
      setSelectedRelease(null);
      showToast('Release restored successfully', 'success');
    }
    if (restoreMutation.isError) {
      showToast(restoreMutation.error?.message || 'Failed to restore release', 'error');
    }
  }, [restoreMutation.isSuccess, restoreMutation.isError]);

  // Handle permanent delete success/error
  React.useEffect(() => {
    if (permanentDeleteMutation.isSuccess) {
      setShowPermanentDeleteDialog(false);
      setSelectedRelease(null);
      showToast('Release permanently deleted', 'success');
    }
    if (permanentDeleteMutation.isError) {
      showToast(permanentDeleteMutation.error?.message || 'Failed to permanently delete release', 'error');
    }
  }, [permanentDeleteMutation.isSuccess, permanentDeleteMutation.isError]);

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDaysUntilDeletionVariant = (days: number): 'error' | 'warning' | 'default' => {
    if (days <= 7) return 'error';
    if (days <= 14) return 'warning';
    return 'default';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message="Failed to load deleted releases" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Release Recycle Bin"
        description="Deleted releases are kept for 30 days before permanent deletion"
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/releases')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Releases
          </Button>
        }
      />

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search by version..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            leftElement={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Releases table */}
      {data?.releases.length === 0 ? (
        <Card className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-zinc-400 mb-4" />
          <h3 className="text-lg font-medium text-zinc-900 mb-2">No deleted releases</h3>
          <p className="text-zinc-500">The recycle bin is empty.</p>
        </Card>
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-200">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Version
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Deleted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Time Until Deletion
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-zinc-200">
                  {data?.releases.map((release) => (
                    <tr key={release.id} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-zinc-900">
                            {release.version}
                          </div>
                          <Badge variant="default" size="sm" className="mt-1">
                            {release.channel}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-zinc-900">
                          {release.platform}/{release.architecture}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">
                        {formatFileSize(release.fileSize)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-zinc-900">
                            {new Date(release.deletedAt).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-zinc-500">
                            by {release.deletedBy?.email || release.deletedBy?.name || 'Unknown'}
                          </div>
                          {release.deletionReason && (
                            <div className="text-sm text-zinc-400 italic">
                              "{release.deletionReason}"
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getDaysUntilDeletionVariant(release.daysUntilPermanentDeletion)}>
                          <Clock className="h-3 w-3 mr-1" />
                          {release.daysUntilPermanentDeletion} days
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedRelease(release);
                              setShowRestoreDialog(true);
                            }}
                            leftIcon={<RefreshCw className="h-4 w-4" />}
                          >
                            Restore
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setSelectedRelease(release);
                              setShowPermanentDeleteDialog(true);
                            }}
                            leftIcon={<Trash2 className="h-4 w-4" />}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-700">
                Showing {(data.page - 1) * data.pageSize + 1} to{' '}
                {Math.min(data.page * data.pageSize, data.totalCount)} of{' '}
                {data.totalCount} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  leftIcon={<ChevronLeft className="h-4 w-4" />}
                >
                  Previous
                </Button>
                <span className="text-sm text-zinc-700">
                  Page {data.page} of {data.totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === data.totalPages}
                  rightIcon={<ChevronRight className="h-4 w-4" />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Restore Dialog */}
      <ConfirmDialog
        isOpen={showRestoreDialog}
        onCancel={() => {
          setShowRestoreDialog(false);
          setSelectedRelease(null);
        }}
        onConfirm={() => {
          if (selectedRelease) {
            restoreMutation.mutate({
              releaseId: selectedRelease.id,
            });
          }
        }}
        title="Restore Release"
        message={`Are you sure you want to restore version ${selectedRelease?.version}?`}
        confirmLabel="Restore"
      />

      {/* Permanent Delete Dialog */}
      <ConfirmDialog
        isOpen={showPermanentDeleteDialog}
        onCancel={() => {
          setShowPermanentDeleteDialog(false);
          setSelectedRelease(null);
        }}
        onConfirm={() => {
          if (selectedRelease) {
            permanentDeleteMutation.mutate({
              releaseId: selectedRelease.id,
            });
          }
        }}
        title="Permanently Delete Release"
        message={`This action cannot be undone! Are you sure you want to permanently delete version ${selectedRelease?.version}? This will remove the release and its binary file from all storage systems.`}
        confirmLabel="Permanently Delete"
        severity="high"
      />
    </div>
  );
}